/**
 * Tartı Tanımları Servisi — SQL `Scales` tablosu
 *
 * Bu servis yalnızca TANIM katmanıdır: hangi tartılar var, nerede, hangi model.
 * Cihazla konuşma (canlı ağırlık, bağlantı testi) local-service üzerinden olur;
 * bkz. services/localScaleService.ts.
 *
 * Tanımın SQL'de olmasının sebebi: aynı cihaza hem kasa (canlı tartım) hem stok
 * ekranı (PLU gönderimi) erişiyor ve envanterin merkezî görülmesi gerekiyor.
 * local-service'in veritabanı sürücüsü yok, listeyi buradan alır.
 *
 * ⚠️ Backend PUT/DELETE kabul etmiyor — güncelleme ve silme POST ile yapılır.
 */

import { salesApi } from '../config/apiClient';

const BASE = '/api/Scale';

export interface ScaleDto {
  id: number;
  name: string;
  description?: string | null;
  host: string;
  port: number;
  /** Protokol sürücüsü; bugün tek değer: "CAS_CL". */
  model: string;
  /** Tartının bastığı ağırlık barkodunun prefix'i (varsayılan "27"). */
  barcodePrefix?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

/** Kaydetme yükü. `id` boşsa yeni tartı açılır. */
export interface SaveScalePayload {
  id?: number | null;
  name: string;
  description?: string | null;
  host: string;
  port: number;
  model: string;
  barcodePrefix?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  isActive: boolean;
}

/**
 * ⚠️ gts_sales_api gövdeyi PascalCase döndürüyor (`Program.cs`:
 * `PropertyNamingPolicy = null`), gelen istekte ise büyük/küçük harfe duyarsız.
 * Bu yüzden okurken iki yazımı da karşılıyoruz, yazarken PascalCase gönderiyoruz.
 */
function unwrapList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.Data)) return raw.Data;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
}

function unwrapItem(raw: any): any {
  return raw?.Data ?? raw?.data ?? raw;
}

/** Sunucudan gelen kaydı frontend'in camelCase tipine çevirir. */
function toScale(raw: any): ScaleDto {
  const pick = <T,>(pascal: string, camel: string): T => (raw?.[pascal] ?? raw?.[camel]) as T;
  return {
    id: pick<number>('Id', 'id'),
    name: pick<string>('Name', 'name'),
    description: pick<string | null>('Description', 'description'),
    host: pick<string>('Host', 'host'),
    port: pick<number>('Port', 'port'),
    model: pick<string>('Model', 'model'),
    barcodePrefix: pick<string | null>('BarcodePrefix', 'barcodePrefix'),
    branchId: pick<string | null>('BranchId', 'branchId'),
    branchName: pick<string | null>('BranchName', 'branchName'),
    isActive: pick<boolean>('IsActive', 'isActive'),
    createdAt: pick<string>('CreatedAt', 'createdAt'),
    updatedAt: pick<string | null>('UpdatedAt', 'updatedAt'),
  };
}

/** Kaydetme gövdesini API'nin beklediği PascalCase'e çevirir. */
function toSaveBody(payload: SaveScalePayload) {
  return {
    Id: payload.id ?? null,
    Name: payload.name,
    Description: payload.description ?? null,
    Host: payload.host,
    Port: payload.port,
    Model: payload.model,
    BarcodePrefix: payload.barcodePrefix ?? null,
    BranchId: payload.branchId || null,
    BranchName: payload.branchName || null,
    IsActive: payload.isActive,
  };
}

export const scaleApi = {
  /**
   * Firmanın tartıları. `activeOnly` kasadaki seçim listesi için: pasif
   * cihazlar orada görünmemeli ama yönetim ekranında görünmeli.
   */
  async getAll(options?: { branchId?: string | null; activeOnly?: boolean }): Promise<ScaleDto[]> {
    const params: Record<string, string> = {};
    if (options?.branchId) params.branchId = options.branchId;
    if (options?.activeOnly) params.activeOnly = 'true';

    const { data } = await salesApi.get(`${BASE}/by-company`, { params });
    return unwrapList(data).map(toScale);
  },

  async getById(id: number): Promise<ScaleDto | null> {
    if (!id || id <= 0) return null;
    const { data } = await salesApi.get(`${BASE}/${id}`);
    const item = unwrapItem(data);
    return item ? toScale(item) : null;
  },

  /** Yeni tartı açar veya var olanı günceller (`id` doluysa güncelleme). */
  async save(payload: SaveScalePayload): Promise<ScaleDto> {
    const { data } = await salesApi.post(`${BASE}/save`, toSaveBody(payload));
    return toScale(unwrapItem(data));
  },

  /** Soft delete. Bu tartıyı seçmiş ekranların seçimi backend'de temizlenir. */
  async remove(id: number): Promise<void> {
    if (!id || id <= 0) throw new Error('Silinecek tartı belirlenemedi.');
    await salesApi.post(`${BASE}/delete`, { Id: id });
  },
};

export default scaleApi;
